import { SeasonID } from '@shared/types';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis } from 'app/constants';
import { useAppSelector } from 'app/hooks';
import { getSelectedYear } from 'app/slices/GamesSlice';
import {
  getRankingsByWeek,
  getSelectedTeamAllRanks,
  getSelectedTeamSelectedSeasonRanks,
} from 'app/slices/RankingSlice';
import { toSeasonWeekID } from 'helpers/IdHelpers';
import { useCallback, useEffect, useState } from 'react';
import { GameWeek, InitRankingFn, IRankingCategories, RankingsPayloadByWeek, ShowBy } from 'types';

const CONSOLE_ON = getConsoleState(ConsoleID.UseAllTeamStatsInfo);

const useAllTeamStatsInfo = (week: GameWeek, year: SeasonID, showBy: ShowBy = 'weekTotals') => {
  const [localSelectedWeek, setLocalSelectedWeek] = useState<GameWeek>(week);
  const [localSelectedYear, setLocalSelectedYear] = useState<SeasonID>(year);
  const [localTeamRanks, setLocalTeamRanks] = useState<IRankingCategories>({});

  const reduxSelectedYear = useAppSelector(getSelectedYear);

  const teamRanks: IRankingCategories = useAppSelector(getSelectedTeamAllRanks);
  const teamRanksAllSeason: IRankingCategories = useAppSelector(getSelectedTeamSelectedSeasonRanks);

  const ranks: RankingsPayloadByWeek = useAppSelector(getRankingsByWeek);

  useEffect(() => {
    if (CONSOLE_ON) console.log(`${Emojis.eyes} Init %c useAllTeamStatsInfo %c`, Cls.Loc, '');
    setLocalSelectedWeek(week);
    setLocalSelectedYear(year);
  }, [week, year]);

  useEffect(() => {
    if (week === GameWeek.Week18) {
      setLocalTeamRanks(teamRanksAllSeason);
    } else {
      setLocalTeamRanks(teamRanks);
    }
  }, [week, teamRanks, teamRanksAllSeason]);

  const initAllTeamsRankings = (initRankings: InitRankingFn) => {
    const msg = `local year %c ${localSelectedYear} %c redux year %c ${reduxSelectedYear} %c`;
    if (CONSOLE_ON) console.log(`${Emojis.question} Is equal ${msg}`, Cls.Bur, '', Cls.Cnt, '');
    const rankingRecord = ranks[toSeasonWeekID(reduxSelectedYear, localSelectedWeek)];
    if (localSelectedWeek && rankingRecord && rankingRecord.rankings && rankingRecord.seasonRankings && showBy) {
      const ranks: IRankingCategories = showBy === 'weekTotals' ? rankingRecord.rankings : rankingRecord.seasonRankings;

      initRankings(ranks);
    }
  };

  const initOneTeamRankings = useCallback(
    (initRankings: InitRankingFn) => {
      // #region LOGGING
      const hasRanks = localTeamRanks && Object.keys(localTeamRanks).length > 0;
      const hasRanksMsg = hasRanks ? '%c has ranks %c initializing...' : '%c no ranks %c skipping init...';
      const colorCls = hasRanks ? Cls.Army : Cls.Cri;
      const msg = `${Emojis.eyes} %c initOneTeamRankings %c ${hasRanksMsg}`;
      if (CONSOLE_ON) console.log(msg, Cls.Loc, '', colorCls, '');
      // #endregion
      if (localTeamRanks) initRankings(localTeamRanks);
    },
    [localTeamRanks]
  );

  return {
    initAllTeamsRankings,
    initOneTeamRankings,
  };
};

export default useAllTeamStatsInfo;
